import { Timestamp, WithFieldValue, serverTimestamp } from 'firebase/firestore';
import { i18n as i18nClient } from 'i18next';
import uuid from 'uuid-random';
import { Weddings } from '@bridebook/models/source/models/Weddings';
import { Events } from '@bridebook/models/source/models/Weddings/Events';
import { IWeddingEvent } from '@bridebook/models/source/models/Weddings/Events.types';
import { QueryClient } from '@tanstack/react-query';
import { eventTypeMap, isCustomEvent, isMainEvent } from '@bridebook/web/lib/events/types';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { invalidateEventsQuery } from '@bridebook/web/lib/wedding-website/hooks/query/events/use-events';
import { IGuest_Invitation } from '@bridebook/models/source/models/Weddings/Guests.types';
import { ExtendedGuestEvent } from '@bridebook/web/lib/rsvp/hooks/use-invitation-responses';

export function getNewMainEvent(
  i18n: i18nClient,
  omitCreatedAt: true,
): Omit<IWeddingEvent, 'createdAt'>;

export function getNewMainEvent(i18n: i18nClient, omitCreatedAt?: false): IWeddingEvent;

export function getNewMainEvent(
  i18n: i18nClient,
  omitCreatedAt?: boolean,
): IWeddingEvent | Omit<IWeddingEvent, 'createdAt'> {
  return {
    id: Events.MAIN_EVENT,
    type: eventTypeMap.MAIN_EVENT,
    name: i18n.t('weddingWebsite:events.mainEventDefaultName'),
    description: '',
    attire: '',
    featured: true,
    ...(!omitCreatedAt && { createdAt: new Timestamp(0, 0) }),
  };
}

export const getNewMainEventWithFieldValue = (i18n?: i18nClient): WithFieldValue<IWeddingEvent> => {
  if (i18n == null) {
    i18n = getI18n();
  }

  return {
    ...getNewMainEvent(i18n),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
};

interface ICreateEventOptions {
  weddingId: string;
  queryClient: QueryClient;
}

export const createMainEvent = async ({ weddingId, queryClient }: ICreateEventOptions) => {
  await Weddings._.getById(weddingId)
    .Events.push(Events.MAIN_EVENT)
    .create(getNewMainEventWithFieldValue());
  invalidateEventsQuery(queryClient, weddingId);
};

export const createMainEventIfNotExists = async ({
  weddingId,
  queryClient,
}: ICreateEventOptions) => {
  const mainEventRef = await Weddings._.getById(weddingId).Events.getById(Events.MAIN_EVENT);
  const mainEvent = await mainEventRef.get();

  if (!mainEvent) {
    await createMainEvent({ weddingId, queryClient });
  }
};

export const getNewCustomWeddingEvent = (): IWeddingEvent => ({
  id: uuid(),
  type: eventTypeMap.CUSTOM_EVENT,
  name: '',
  date: '',
  description: '',
  attire: '',
  featured: true,
  supplier: '',
  createdAt: new Timestamp(0, 0),
});

// get the main event from the events array - technically we can only have one main event, but it's an array so it's plural
export const getMainEvents = (events: IWeddingEvent[]) =>
  events.filter((event) => isMainEvent(event));

// get the custom events from the events array
export const getCustomEvents = (events: IWeddingEvent[]) =>
  events.filter((event) => isCustomEvent(event));

export const enrichEventWithGuestEventData = (
  guestRsvp: IGuest_Invitation | null | undefined,
  event: IWeddingEvent,
): ExtendedGuestEvent => ({
  event,
  eventId: event.id,
  rsvp: guestRsvp?.events[event.id]?.rsvp,
  invited: !!guestRsvp?.events[event.id],
});
